import { DEFAULT_PAGE_SIZE } from '@/constants';
import type { CreditNoteSorting, ProcessedOrderSorting } from '@/generated';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const PAGINATOR_KEYS = {
    PROCESSED_ORDERS: 'processedOrders',
    CREDIT_NOTES: 'creditNotes',
    SPECIAL_CASES: 'specialCases',
} as const;

export type PaginatorKey = (typeof PAGINATOR_KEYS)[keyof typeof PAGINATOR_KEYS];

type PaginatorState = {
    pageIndex: number;
    pageSize: number;
};

type PaginatorsState = Record<PaginatorKey, PaginatorState>;

/** This store enables only navigation through the order list and its single elements managed
 * by TSQ in orders.ts */
const usePaginatorStore = defineStore('paginator', () => {
    const paginators = ref<PaginatorsState>({
        [PAGINATOR_KEYS.PROCESSED_ORDERS]: {
            pageIndex: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        },
        [PAGINATOR_KEYS.CREDIT_NOTES]: {
            pageIndex: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        },
        [PAGINATOR_KEYS.SPECIAL_CASES]: {
            pageIndex: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        },
    });
    const sortBy = ref<CreditNoteSorting | ProcessedOrderSorting>();

    const getPaginator = (key: PaginatorKey) => ({
        pageIndexRef: computed<number>({
            get: () => paginators.value[key].pageIndex,
            set: value => {
                paginators.value[key].pageIndex = value;
            },
        }),
        pageSizeRef: computed<number>({
            get: () => paginators.value[key].pageSize,
            set: value => {
                paginators.value[key].pageSize = value;
            },
        }),
    });

    const resetPaginator = (key: PaginatorKey) => {
        paginators.value[key] = {
            pageIndex: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        };
    };

    return {
        paginators,
        getPaginator,
        resetPaginator,
        sortBy,
    };
});

export default usePaginatorStore;
